import { useEffect, useRef } from "react";
import { useZero } from "@rocicorp/zero/react";

const HIDDEN_THRESHOLD_MS = 60_000;

/**
 * Pre-empts Zero's stale-token auto-reconnect race when the tab returns
 * from being hidden.
 *
 * Without this, the timeline for a 20-min-hidden tab is:
 *   1. Zero auto-disconnects after `hiddenTabDisconnectDelay`
 *   2. Browser throttles our refresh timer → auth token expires
 *   3. Tab returns → Zero auto-reconnects with the cached (stale) token
 *   4. Server returns 401 → Zero enters needs-auth (terminal state)
 *   5. ZeroAuthRecovery fires but its refresh chain may take a moment to
 *      run, leaving the IVM cleared by CVR `del` patches in the gap
 *
 * With this component:
 *   1. Tab returns after >60s hidden → ask the session-token hook for a
 *      fresh auth token via `refreshZeroSessionToken`
 *   2. Call zero.connection.connect({ auth: fresh }), which either
 *      - exits needs-auth with the fresh token (if Zero already 401'd), or
 *      - silently updates Zero's cached auth so the next reconnect uses it
 *
 * Quick tab switches (<60s hidden) skip the refresh to avoid hammering
 * the refresh endpoint.
 *
 * `refreshZeroSessionToken` delegates to `useZeroSessionToken.refreshToken`,
 * which returns the right kind of token based on the feature flag:
 *
 *  - `VITE_ENABLE_ZERO_SESSION_AUTH=true`: a fresh Zero session JWT (12h TTL)
 *  - `VITE_ENABLE_ZERO_SESSION_AUTH=false`: a force-refreshed WorkOS token
 *
 * This means ZeroVisibilityRefresh stays flag-agnostic and never has to
 * know which token Zero is actually using. Before this fix, it hardcoded
 * `getAccessToken({forceRefresh: true})` — which silently swapped Zero's
 * stored auth from a Zero JWT to a WorkOS token when the new path was on,
 * causing the Zero JWT to be abandoned and subsequent mutations to hit 401
 * once the shorter-TTL WorkOS token expired.
 *
 * The `refreshToken` mutex inside the session-token hook coordinates with
 * ZeroAuthRecovery so concurrent refreshes share a single in-flight call.
 *
 * Must be mounted as a child of `<ZeroProvider>` so `useZero()` works.
 */
export function ZeroVisibilityRefresh({
  refreshZeroSessionToken,
}: {
  refreshZeroSessionToken: () => Promise<string | null>;
}) {
  const zero = useZero();
  const lastVisibleAtRef = useRef(Date.now());
  const inFlightRef = useRef(false);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") {
        lastVisibleAtRef.current = Date.now();
        return;
      }
      const hiddenDuration = Date.now() - lastVisibleAtRef.current;
      lastVisibleAtRef.current = Date.now();

      if (hiddenDuration < HIDDEN_THRESHOLD_MS) return;
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      try {
        const fresh = await refreshZeroSessionToken();
        if (fresh) {
          await zero.connection.connect({ auth: fresh });
        }
      } catch (err) {
        console.warn("[ZeroVisibilityRefresh] failed:", err);
      } finally {
        inFlightRef.current = false;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refreshZeroSessionToken, zero]);

  return null;
}
